import { useState, lazy, Suspense } from 'react'
import '../styles/animations.css'
import RotatingText from '../components/reactbits/RotatingText'
import profileImage from '../assets/images/profile/profilePic.png'
import emojiLoop from '../assets/video/igo-loop.webp'
import TextType from '../components/reactbits/TextType'
import { TECH_LOGOS } from '../data/tech_logos'
import { useIsMobile } from '../hooks/useIsMobile'
import LogoLoop from '../components/reactbits/LogoLoop'
import { useLanguage } from '../context/LanguageContext'
import { useIsDark } from '../hooks/useIsDark'
import { t } from '../i18n'

const Grainient = lazy(() => import('../components/reactbits/Grainient'))

function Home({ onGrainientReady, started }) {
	const { language } = useLanguage()
	const isDark = useIsDark()
	const fullName = 'Rodrigo Alvarez Teytud'
	const shortName = 'Igo'
	const [isProfileHovered, setIsProfileHovered] = useState(false)
	const [emojiPlayId, setEmojiPlayId] = useState(0)

	const isMobile = useIsMobile()

	const words = t(language, 'home.roles')

	const handleMouseEnter = () => {
		// Nueva sesión de flip: fuerza reinicio del WebP (el browser cachea la animación)
		const playId = Date.now()
		setEmojiPlayId(playId)
		setIsProfileHovered(true)
	}

	const handleMouseLeave = () => {
		// Al volver a la foto, desmontamos el emoji para dejarlo listo al siguiente flip
		setIsProfileHovered(false)
	}

	return (
		<section
			id='home'
			className='overflow-x-hidden'
		>
			<div className='min-h-screen flex flex-col items-center justify-center relative overflow-hidden transition-colors duration-300' style={{ backgroundColor: isDark ? '#364156' : '#c8d6e5' }}>
				<div className='absolute inset-0 z-0 pointer-events-none transition-colors duration-300' style={{ backgroundColor: isDark ? '#364156' : '#c8d6e5' }}>
					<Suspense fallback={null}>
						<Grainient
							color1={isDark ? '#364156' : '#c8d6e5'}
							color2={isDark ? '#4c4b4e' : '#dfe6ed'}
							color3='#F97316'
							timeSpeed={0.25}
							colorBalance={0}
							warpStrength={1}
							warpFrequency={5}
							warpSpeed={2}
							warpAmplitude={50}
							blendAngle={0}
							blendSoftness={0.05}
							rotationAmount={500}
							noiseScale={2}
							grainAmount={isDark ? 0.1 : 0.05}
							grainScale={2}
							grainAnimated={false}
							contrast={isDark ? 1.5 : 1.2}
							gamma={1}
							saturation={isDark ? 1 : 0.9}
							centerX={0}
							centerY={0}
							zoom={0.9}
						/>
					</Suspense>
				</div>

				<div
					className='home-bottom-fade'
					aria-hidden='true'
					style={{
						background: isDark
							? 'linear-gradient(to bottom, transparent 0%, rgba(51, 65, 85, 0.35) 45%, rgb(51, 65, 85) 100%)'
							: 'linear-gradient(to bottom, transparent 0%, rgba(241, 245, 249, 0.5) 40%, rgb(241, 245, 249) 100%)'
					}}
				/>
				<div className='relative z-10 w-full min-h-screen flex flex-col items-center justify-center overflow-x-hidden max-w-full px-4'>
					<div
						className='profile-flip-container'
						onMouseEnter={handleMouseEnter}
						onMouseLeave={handleMouseLeave}
					>
						<div className='profile-flip-card md:w-[250px] md:h-[250px] w-[150px] h-[150px]'>
							<div className='profile-front'>
								<img
									src={profileImage}
									alt={t(language, 'home.profileAlt')}
									className='profile-image'
									fetchPriority='high'
									width='250'
									height='250'
								/>
							</div>
							<div className='profile-back'>
								{isProfileHovered && (
									<img
										key={emojiPlayId}
										src={`${emojiLoop}?t=${emojiPlayId}`}
										alt={t(language, 'home.videoAlt')}
										className='profile-image'
										width='250'
										height='250'
										decoding='async'
										draggable={false}
									/>
								)}
							</div>
						</div>
					</div>
					<h1 className={`font-bold font-mono text-center mb-[10px] z-[1] text-[clamp(1.75rem,9vw,2rem)] md:text-[5rem] transition-colors duration-300 ${isDark ? 'text-white' : 'text-slate-900'}`}>
					<TextType
								key={isProfileHovered ? 'short' : 'full'}
								as='span'
								text={isProfileHovered ? shortName : fullName}
								typingSpeed={80}
								deletingSpeed={40}
								loop={false}
								showCursor={true}
								hideCursorWhileTyping={false}
								started={started}
							/>
					</h1>
					<h2 className={`font-medium z-[1] text-[1rem] md:text-[1.5rem] mb-10 transition-colors duration-300 ${isDark ? 'text-white/90' : 'text-slate-700'}`}>
						<RotatingText
							texts={words}
							mainClassName='justify-center'
							staggerFrom='last'
							ease='bounce.out'
							staggerDuration={0.025}
							rotationInterval={4000}
							splitLevelClassName='overflow-hidden'
						/>
					</h2>
					{isMobile && (
						<div className='w-full max-w-full overflow-hidden'>
							<LogoLoop
								logos={TECH_LOGOS}
								speed={20}
								direction='left'
								logoHeight={40}
								gap={35}
								hoverSpeed={0}
								scaleOnHover
								fadeOut
								fadeOutColor='transparent'
								ariaLabel={t(language, 'home.techPartners')}
							/>
						</div>
					)}

					<div className='hidden lg:flex gap-[30px] absolute bottom-[110px] left-1/2 -translate-x-1/2'>
						{TECH_LOGOS.map((logo) => (
							<a
								key={logo.alt}
								className='floating-logo'
								href={logo.href}
								data-tech={logo.title}
								target='_blank'
								rel='noreferrer'
								style={{
									'--circle-color': logo.circleColor
								}}
							>
								<img
									src={logo.src}
									alt={`${logo.alt} Logo`}
									width='60'
									height='60'
									loading='lazy'
									style={logo.imgStyle}
								/>
								<span className='tech-text'>{logo.title}</span>
							</a>
						))}
					</div>
				</div>
			</div>
		</section>
	)
}

export default Home
